/**
 * Markdown — a small, dependency-free standard-Markdown → React renderer.
 *
 * This is the app's reusable markdown module. It renders a practical subset of
 * CommonMark (what the team actually writes in RFI responses, notes, etc.):
 *   - headings           # / ## / ### (rendered bold, body-size, not oversized)
 *   - bold               **text** or __text__
 *   - italic             *text* or _text_
 *   - underline          ++text++
 *   - inline code        `code`
 *   - links              [label](url) and bare https:// URLs
 *   - bullet lists       -, *, or • prefixed lines, NESTED by 2-space indent per level
 *   - numbered lists     1.  2.  … (renders an <ol>; markdown auto-numbers), same nesting rule
 *   - paragraphs / line breaks (blank line = new paragraph; single newline = <br>)
 *   - variable highlight  text wrapped in the spec-engine sentinels (U+E000 … U+E001)
 *                         renders as a highlighted <mark> (see specEngine VAR_OPEN/VAR_CLOSE)
 *   - spec tables         lines between @@MTX@@ and @@/MTX@@ marker lines, cells split by '|',
 *                         first row the header (AlderSPEC's Matrix of Responsibility; the PDF
 *                         draws the same region in api/spec-pdf.js#drawMatrixTable)
 *
 * Output inherits font-size/family from its container (so it matches surrounding
 * outline text). No HTML injection — everything is built as React elements.
 *
 * Usage: <Markdown text={rfi.response} />
 */

const VAR_OPEN = String.fromCharCode(0xE000)
const VAR_CLOSE = String.fromCharCode(0xE001)
const VAR_SEP = String.fromCharCode(0xE002)
const INDENT_MARK = String.fromCharCode(0xE010)  // leading INDENT_MARK + digit = outline indent level

// Order matters: the variable-highlight sentinel pair is matched first.
const INLINE = new RegExp(
  '(' + VAR_OPEN + '[^' + VAR_CLOSE + ']*' + VAR_CLOSE +
  '|`[^`]+`|\\*\\*[^*]+\\*\\*|__[^_]+__|\\+\\+[^+]+\\+\\+|\\[[^\\[\\]]+\\]\\((?:[^()\\s]|\\([^()\\s]*\\))+\\)|https?:\\/\\/[^\\s)]+|\\*[^*\\n]+\\*|_[^_\\n]+_)',
  'g'
)

function renderInline(text, keyBase, onVarClick) {
  if (!text) return null
  const out = []
  let last = 0, k = 0, m
  INLINE.lastIndex = 0
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    const key = keyBase + '-' + (k++)
    if (tok.charCodeAt(0) === 0xE000) {
      // payload = varKey + VAR_SEP + value  (varKey absent in legacy/no-sep tokens)
      const inner = tok.slice(1, -1)
      const sep = inner.indexOf(VAR_SEP)
      const varKey = sep >= 0 ? inner.slice(0, sep) : ''
      const value = sep >= 0 ? inner.slice(sep + 1) : inner
      const clickable = !!onVarClick && !!varKey
      out.push(
        <mark key={key}
          onClick={clickable ? (e) => { e.preventDefault(); e.stopPropagation(); onVarClick(varKey) } : undefined}
          title={clickable ? 'Edit this setting' : undefined}
          style={{
            background: 'rgba(245,200,60,0.34)', color: 'inherit', borderRadius: 2, padding: '0 2px',
            cursor: clickable ? 'pointer' : undefined,
            textDecoration: clickable ? 'underline dotted rgba(7,39,45,0.4)' : undefined,
            textUnderlineOffset: clickable ? 2 : undefined,
          }}>{value}</mark>
      )
    } else if (tok.startsWith('`')) {
      out.push(<code key={key} style={{ fontFamily: '"DM Mono",monospace', fontSize: '0.92em', background: 'rgba(7,39,45,0.06)', borderRadius: 3, padding: '1px 4px' }}>{tok.slice(1, -1)}</code>)
    } else if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(<strong key={key}>{tok.slice(2, -2)}</strong>)
    } else if (tok.startsWith('++')) {
      out.push(<u key={key}>{tok.slice(2, -2)}</u>)
    } else if (tok.startsWith('[')) {
      // The url starts after "](", not at the first "(": citations like "25.10.008(E)(2)" carry
      // parentheses in the label, and the label holds no brackets ("[[LBMC …](url)]").
      const lbl = tok.slice(1, tok.indexOf(']('))
      const url = tok.slice(tok.indexOf('](') + 2, -1)
      out.push(<a key={key} href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1D6FA8', textDecoration: 'underline', wordBreak: 'break-word' }}>{lbl}</a>)
    } else if (tok.startsWith('http')) {
      out.push(<a key={key} href={tok} target="_blank" rel="noopener noreferrer" style={{ color: '#1D6FA8', textDecoration: 'underline', wordBreak: 'break-word' }}>{tok}</a>)
    } else if (tok.startsWith('*') || tok.startsWith('_')) {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>)
    } else {
      out.push(tok)
    }
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

// Recursively render a nested list node ({ ordered, depth, items: [{ text, children }] }) into
// a real <ul>/<ol> with child lists nested inside their parent <li>, matching the 2-space-per-
// level indent convention MarkdownField's Indent/Outdent buttons write.
function renderListNode(list, keyPrefix, onVarClick) {
  const items = list.items.map((it, i) => (
    <li key={keyPrefix + '-' + i} style={{ marginBottom: 2 }}>
      {renderInline(it.text, keyPrefix + '-' + i, onVarClick)}
      {it.children ? renderListNode(it.children, keyPrefix + '-' + i + 'c', onVarClick) : null}
    </li>
  ))
  return list.ordered
    ? <ol key={keyPrefix} style={{ margin: '2px 0 6px', paddingLeft: 20 }}>{items}</ol>
    : <ul key={keyPrefix} style={{ margin: '2px 0 6px', paddingLeft: 18 }}>{items}</ul>
}

export default function Markdown({ text, style, onVarClick }) {
  if (text == null || text === '') return null
  const lines = String(text).replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let para = []        // accumulating paragraph lines
  let listStack = []   // open lists by depth: [{ ordered, depth, items: [{ text, children }] }]

  const flushPara = () => {
    if (!para.length) return
    const key = 'p' + blocks.length
    const nodes = []
    para.forEach((ln, i) => {
      if (i > 0) nodes.push(<br key={key + '-br' + i} />)
      const inner = renderInline(ln.text, key + '-' + i, onVarClick)
      nodes.push(ln.indent
        ? <span key={key + '-l' + i} style={{ display: 'inline-block', marginLeft: ln.indent * 18 }}>{inner}</span>
        : <span key={key + '-l' + i}>{inner}</span>)
    })
    blocks.push(<p key={key} style={{ margin: '0 0 6px' }}>{nodes}</p>)
    para = []
  }
  // Close the deepest open list, attaching it as the `children` of whatever is now the
  // (new) deepest open list's last item — or, if the stack is now empty, pushing it as a
  // top-level block. This is what turns a run of variably-indented bullet lines into a real
  // parent/child <li> tree instead of one flat list.
  const closeTopList = () => {
    const closed = listStack.pop()
    if (listStack.length) {
      const parent = listStack[listStack.length - 1]
      parent.items[parent.items.length - 1].children = closed
    } else {
      blocks.push(renderListNode(closed, 'list' + blocks.length, onVarClick))
    }
  }
  const flushList = () => { while (listStack.length) closeTopList() }
  // Add one list line at the given nesting depth. Closes deeper open lists first; opens a new
  // (possibly nested) list when depth increases or the list type (bullet vs numbered) changes.
  const pushListLine = (depth, ordered, text) => {
    while (listStack.length && listStack[listStack.length - 1].depth > depth) closeTopList()
    const top = listStack[listStack.length - 1]
    if (!top || top.depth < depth) {
      listStack.push({ ordered, depth, items: [] })
    } else if (top.ordered !== ordered) {
      closeTopList()
      listStack.push({ ordered, depth, items: [] })
    }
    listStack[listStack.length - 1].items.push({ text, children: null })
  }

  // @@MTX@@ … @@/MTX@@ — a spec table region (the Matrix of Responsibility). Rows are '|'-separated
  // cells, the first row is the header. Drawn the way api/spec-pdf.js#drawMatrixTable draws it:
  // a wider first column, a shaded bold header, a hairline under each row.
  let tableRows = null
  const flushTable = () => {
    if (!tableRows) return
    const rows = tableRows; tableRows = null
    if (!rows.length) return
    const key = 't' + blocks.length
    const cols = Math.max(...rows.map(r => r.length))
    const cell = { padding: '4px 8px', borderBottom: '0.5px solid var(--aj-border, rgba(7,39,45,0.15))', textAlign: 'left', verticalAlign: 'top' }
    blocks.push(
      <table key={key} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', margin: '6px 0 8px', fontSize: '0.95em' }}>
        <colgroup>{Array.from({ length: cols }, (_, i) => <col key={i} style={{ width: (100 * (i === 0 ? 1.8 : 1) / (cols + 0.8)) + '%' }} />)}</colgroup>
        <thead>
          <tr style={{ background: 'var(--aj-bg-subtle, rgba(7,39,45,0.05))' }}>
            {Array.from({ length: cols }, (_, i) => <th key={i} style={{ ...cell, fontWeight: 600 }}>{renderInline(rows[0][i] || '', key + '-h' + i, onVarClick)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((r, ri) => (
            <tr key={ri}>
              {Array.from({ length: cols }, (_, i) => <td key={i} style={cell}>{renderInline(r[i] || '', key + '-' + ri + '-' + i, onVarClick)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  for (const raw of lines) {
    if (raw.trim() === '@@MTX@@') { flushPara(); flushList(); flushTable(); tableRows = []; continue }
    if (raw.trim() === '@@/MTX@@') { flushTable(); continue }
    if (tableRows) { if (raw.trim()) tableRows.push(raw.split('|').map(c => c.trim())); continue }
    let line = raw.replace(/\s+$/, '')
    // leading INDENT_MARK + digit → outline indent level (from the spec line editor's flatten)
    let indent = 0
    if (line.charCodeAt(0) === 0xE010) { indent = parseInt(line[1], 10) || 0; line = line.slice(2) }
    if (line.trim() === '') { flushPara(); flushList(); continue }

    const heading = line.match(/^(#{1,4})\s+(.*)$/)
    const bullet  = line.match(/^(\s*)[-*•]\s+(.*)$/)
    const ordered = line.match(/^(\s*)\d+[.)]\s+(.*)$/)

    if (heading) {
      flushPara(); flushList()
      blocks.push(<div key={'h' + blocks.length} style={{ fontWeight: 600, margin: '4px 0 2px' }}>{renderInline(heading[2], 'h' + blocks.length, onVarClick)}</div>)
    } else if (bullet) {
      flushPara()
      pushListLine(Math.floor(bullet[1].length / 2), false, bullet[2])
    } else if (ordered) {
      flushPara()
      pushListLine(Math.floor(ordered[1].length / 2), true, ordered[2])
    } else {
      flushList()
      para.push({ text: line, indent })
    }
  }
  flushPara(); flushList(); flushTable()

  return <div style={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word', ...style }}>{blocks}</div>
}
